import React, { useState } from 'react';
import { Grid, Typography, Box, IconButton } from '@material-ui/core';
import withStyles from '@material-ui/styles/withStyles';
import { useDropzone } from 'react-dropzone';
import {
  ALLOWED_FILE_TYPES,
  MAX_UPLOAD_FILE_SIZE,
} from 'features/wells/corrosion/helpers/corrosionlogs.constants';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CloseIcon from '@material-ui/icons/Close';

const AddLogFilesDrawerUploadSection = ({
  classes,
  isSubmitting,
  uploadedFiles,
  setUploadedFiles,
}) => {
  const [errorMsg, setErrorMsg] = useState('');
  const allowedFileTypes = ALLOWED_FILE_TYPES;
  const maxUploadFileSize = MAX_UPLOAD_FILE_SIZE;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles, fileRejections) => {
      setErrorMsg('');
      const fileSizeParameter = maxUploadFileSize / (1024 * 1024);

      if (fileRejections.length > 0) {
        const { errors } = fileRejections[0];
        const tooLargeError = errors.find((e) => e.code === 'file-too-large');
        if (tooLargeError) {
          setErrorMsg(
            `File is too large: maximum size is ${fileSizeParameter} MB.`,
          );
        } else {
          setErrorMsg(errors.map((e) => e.message).join(', '));
        }
        return;
      }

      const file = acceptedFiles[0];

      const ext = file.name.split('.').pop()?.toLowerCase();
      const formattedExtensionsParameter = allowedFileTypes
        .map((ext) => ` .${ext}`)
        .join(' or ');

      if (!allowedFileTypes.includes(ext)) {
        setErrorMsg(
          `Invalid file type. Please upload an Excel file (${formattedExtensionsParameter}).`,
        );
        return;
      }

      if (file.size > maxUploadFileSize) {
        setErrorMsg(
          `File is too large: maximum size is ${fileSizeParameter} MB.`,
        );
        return;
      }

      setUploadedFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
    },

    disabled: isSubmitting,
    noClick: false,
    noKeyboard: true,
    maxSize: MAX_UPLOAD_FILE_SIZE,
    multiple: false,
    maxFiles: 1,
    accept: allowedFileTypes.map((ext) => `.${ext}`).join(','),
  });

  const clearUploadedFiles = () => setUploadedFiles([]);

  return (
    <>
      <Grid item>
        <Typography variant="h6">Step 2: Upload</Typography>
      </Grid>

      {uploadedFiles.length === 0 && (
        <Grid
          item
          xs={12}
          alignItems="center"
          className={classes.uploadContainer}
        >
          <Box
            {...getRootProps()}
            className={`${classes.dropzone} ${
              isDragActive ? classes.dragActive : ''
            }`}
          >
            <input
              {...getInputProps()}
              accept={allowedFileTypes.map((ext) => `.${ext}`).join(',')}
            />

            <Grid item className={classes.buttonContainer}>
              <CloudUploadIcon />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                <span className={classes.uploadText}> Click to upload</span> or
                drag and drop the file
              </Typography>
              <Typography className={classes.textStyle}>
                Only Excel spreadsheets are supported. (max. 3MB)
              </Typography>
            </Grid>
          </Box>
        </Grid>
      )}
      {errorMsg && (
        <Typography className={classes.errorText}>{errorMsg}</Typography>
      )}
      {uploadedFiles.length > 0 && (
        <Grid container className={classes.fileListContainer}>
          <Grid item xs={11}>
            {uploadedFiles.map((file) => {
              return (
                <>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    gutterBottom
                  >
                    {file.name}
                  </Typography>
                  <Typography>
                    {(Number(file.size) / 1024).toFixed(2)} KB
                  </Typography>
                </>
              );
            })}
          </Grid>
          <Grid item xs={1}>
            <IconButton title="Close" onClick={() => clearUploadedFiles()}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      )}
    </>
  );
};

const styles = (theme) => ({
  uploadContainer: {
    padding: theme.spacing(2),
    margin: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    border: '2px dashed grey',
    cursor: 'pointer',
  },
  buttonContainer: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  },
  fileListContainer: {
    display: 'flex',
    alignItems: 'left',
    marginTop: theme.spacing(2),
  },

  textStyle: {
    marginTop: theme.spacing(2),
    color: theme.palette.secondary.light,
  },
  uploadText: {
    color: theme.palette.action.selected,
    textDecoration: 'underline',
  },
  errorText: {
    color: theme.palette.error.main,
    paddingTop: theme.spacing(2),
  },
});

export default withStyles(styles)(AddLogFilesDrawerUploadSection);
